import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const { login } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      background: '#fff',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* ── Left panel — form ──────────────────────────────────── */}
      <div style={{
        width: '45%', minWidth: 400,
        height: '100vh',
        display: 'flex', flexDirection: 'column',
        padding: '48px 64px',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <img
            src="/billiontech-logo.png"
            alt="BillionTech"
            style={{ height: 36, width: 'auto', objectFit: 'contain' }}
            onError={e => {
              // Fallback if image doesn't load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Fallback text logo */}
          <div style={{ display: 'none', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'var(--bt-orange)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 18, fontWeight: 900,
            }}>✕</div>
            <span style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800, fontSize: 22, color: 'var(--bt-gray-900)',
            }}>BillionTech</span>
          </div>
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          maxWidth: 400,
          width: '100%',
        }}>
        <h1 style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 26, fontWeight: 800,
          color: 'var(--bt-orange)',
          margin: '0 0 6px',
        }}>Welcome. Let's Login</h1>
        <p style={{ color: 'var(--bt-gray-400)', fontSize: 13, margin: '0 0 36px' }}>
          Welcome back! Please enter your details.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label className="bt-label" style={{ marginBottom: 6 }}>
              USERNAME <span style={{ color: 'var(--bt-orange)' }}>*</span>
            </label>
            <input
              type="text"
              className="bt-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="email@company.com"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="bt-label" style={{ marginBottom: 6 }}>
              PASSWORD <span style={{ color: 'var(--bt-orange)' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwd ? 'text' : 'password'}
                className="bt-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
                style={{ paddingRight: 40 }}
              />
              <button type="button"
                onClick={() => setShowPwd(s => !s)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--bt-gray-400)', fontSize: 13,
                }}>
                {showPwd ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading}
            style={{
              width: '100%', padding: '11px',
              background: 'var(--bt-orange)',
              color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 700,
              fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              fontFamily: 'inherit',
              marginTop: 4,
              transition: 'background 0.15s',
            }}
            onMouseOver={e => { if (!loading) e.target.style.background = 'var(--bt-orange-hover)'; }}
            onMouseOut={e => { e.target.style.background = 'var(--bt-orange)'; }}
          >
            {loading ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <p style={{ marginTop: 24, fontSize: 12, color: 'var(--bt-gray-400)', textAlign: 'center' }}>
          Payment Terms Engine · Powered by BillionTech
        </p>
        </div>
      </div>

      {/* ── Right panel — illustration ─────────────────────────── */}
      <div style={{
        flex: 1,
        height: '100vh',
        background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 50%, #FED7AA 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute', width: 400, height: 400,
          borderRadius: '50%',
          background: 'rgba(249,115,22,0.08)',
          top: -100, right: -100,
        }}/>
        <div style={{
          position: 'absolute', width: 300, height: 300,
          borderRadius: '50%',
          background: 'rgba(249,115,22,0.06)',
          bottom: -80, left: -60,
        }}/>

        {/* Central card */}
        <div style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(8px)',
          borderRadius: 20,
          padding: '40px 48px',
          border: '1px solid rgba(255,255,255,0.8)',
          boxShadow: '0 20px 60px rgba(249,115,22,0.1)',
          textAlign: 'center',
          maxWidth: 380,
          position: 'relative', zIndex: 1,
        }}>
          {/* Logo image */}
          <div style={{ marginBottom: 24 }}>
            <img
              src="/billiontech-logo.png"
              alt="BillionTech"
              style={{ height: 40, width: 'auto', objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>

          <h2 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 22, fontWeight: 800,
            color: 'var(--bt-gray-900)', margin: '0 0 10px',
          }}>Payment Terms Engine</h2>
          <p style={{ fontSize: 13, color: 'var(--bt-gray-500)', margin: '0 0 24px', lineHeight: 1.6 }}>
            Manage POs, payment schedules, and revenue recognition with Ind AS 115 compliance.
          </p>

          {/* Feature pills */}
          {['SO Management', 'Invoice Automation', 'Revenue Recognition', 'Unbilled Revenue', 'Audit Trail'].map(f => (
            <div key={f} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              margin: '3px', padding: '4px 10px',
              background: 'var(--bt-orange-light)',
              border: '1px solid var(--bt-orange-border)',
              borderRadius: 20, fontSize: 11, fontWeight: 600,
              color: 'var(--bt-orange)',
            }}>
              <span style={{ fontSize: 8 }}>●</span> {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
